import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createSession, fetchMe, logout as apiLogout, ApiError, type User } from "./api.js";
import { GOOGLE_CLIENT_ID } from "./config.js";

// --- Types minimaux pour Google Identity Services (chargé dynamiquement) ---
interface CredentialResponse {
  credential: string;
}
interface GoogleId {
  initialize(cfg: {
    client_id: string;
    callback: (resp: CredentialResponse) => void;
    auto_select?: boolean;
  }): void;
  renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
  disableAutoSelect(): void;
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } };
  }
}

type AuthState =
  | { status: "loading" }
  | { status: "anon"; error: string | null }
  | { status: "authed"; user: User; isOwner: boolean; isEditor: boolean };

interface AuthContextValue {
  state: AuthState;
  onCredential: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        setState(
          me
            ? { status: "authed", user: me.user, isOwner: me.isOwner, isEditor: me.isEditor }
            : { status: "anon", error: null },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "anon", error: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onCredential = useCallback(async (credential: string) => {
    try {
      await createSession(credential);
      const me = await fetchMe();
      if (me) {
        setState({ status: "authed", user: me.user, isOwner: me.isOwner, isEditor: me.isEditor });
      }
    } catch (err) {
      const msg =
        err instanceof ApiError && err.code === "not_allowed"
          ? "Cet email n'est pas autorisé. Demande à Max de t'ajouter."
          : "Connexion impossible. Réessaie.";
      setState({ status: "anon", error: msg });
    }
  }, []);

  const logout = useCallback(async () => {
    window.google?.accounts.id.disableAutoSelect();
    await apiLogout();
    setState({ status: "anon", error: null });
  }, []);

  return (
    <AuthContext.Provider value={{ state, onCredential, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

const GSI_SRC = "https://accounts.google.com/gsi/client";

function loadGsi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("GSI load error")));
      return;
    }
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("GSI load error"));
    document.head.appendChild(script);
  });
}

/** Bouton « Se connecter avec Google » rendu par GSI. */
export function GoogleLoginButton() {
  const { onCredential } = useAuth();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    loadGsi()
      .then(() => {
        if (cancelled || !ref.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => {
            void onCredential(resp.credential);
          },
        });
        window.google.accounts.id.renderButton(ref.current, {
          theme: "filled_black",
          size: "large",
          shape: "pill",
          text: "signin_with",
          locale: "fr",
        });
      })
      .catch(() => {
        /* affiché via l'absence de bouton ; l'utilisateur peut recharger */
      });
    return () => {
      cancelled = true;
    };
  }, [onCredential]);

  return <div ref={ref} data-testid="google-login" />;
}
